import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";

export interface SeoLandingProps {
  title: string;
  metaTitle: string;
  metaDescription: string;
  subtitle: string;
  intro: string;
  h2What: string;
  whatParagraphs: string[];
  featuresTitle: string;
  features: string[];
  applicationsTitle: string;
  applicationsParagraphs: string[];
  howToTitle: string;
  howToSteps: { name: string; text: string }[];
  faqs: { q: string; a: string }[];
  primaryKeyword: string;
  slug: string;
}

const BASE_URL = "https://www.naklejki-3d.com.pl";

const SeoLandingPage = (props: SeoLandingProps) => {
  const url = `${BASE_URL}/${props.slug}`;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: props.title,
    description: props.metaDescription,
    brand: { "@type": "Brand", name: "Naklejki-3d.com.pl" },
    manufacturer: { "@type": "Organization", name: "DRUKEXPERT.PL Sp. z o.o." },
    category: "Naklejki reklamowe 3D",
    image: `${BASE_URL}/favicon.png`,
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "PLN",
      lowPrice: "0.50",
      highPrice: "50.00",
      availability: "https://schema.org/InStock",
      offerCount: "15",
      url,
      seller: { "@type": "Organization", name: "Naklejki-3d.com.pl" },
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "127",
      bestRating: "5",
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: props.howToTitle,
    description: `${props.howToTitle} — krok po kroku.`,
    step: props.howToSteps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Strona główna", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: props.title, item: url },
    ],
  };

  return (
    <Layout>
      <SEOHead title={props.metaTitle} description={props.metaDescription} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      {/* Hero */}
      <section className="gradient-navy py-12 md:py-20">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="w-4 h-4 text-accent" />
            <span className="text-accent text-sm font-medium">Producent B2B · Polska</span>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-4 leading-tight">
            {props.title}
          </h1>
          <p className="text-base md:text-xl text-primary-foreground/80 max-w-3xl mx-auto mb-8 leading-relaxed">
            {props.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/kalkulator"
              className="gradient-gold text-accent-foreground font-bold px-8 py-4 rounded-lg hover:opacity-90 transition-opacity shadow-gold inline-flex items-center justify-center gap-2"
            >
              Wycena online w 15 sekund <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/kontakt"
              className="border-2 border-primary-foreground/30 text-primary-foreground font-semibold px-8 py-4 rounded-lg hover:border-accent hover:text-accent transition-colors inline-flex items-center justify-center"
            >
              Zapytaj o ofertę B2B
            </Link>
          </div>
        </div>
      </section>

      {/* Direct answer — AI-friendly */}
      <section className="py-10 md:py-14 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-secondary/50 border-l-4 border-accent rounded-r-lg p-5 md:p-6">
            <p className="text-foreground text-base md:text-lg leading-relaxed">
              <strong>{props.primaryKeyword}</strong> {props.intro}
            </p>
          </div>
        </div>
      </section>

      {/* What is it */}
      <section className="py-10 md:py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">{props.h2What}</h2>
          <div className="space-y-4">
            {props.whatParagraphs.map((p, i) => (
              <p key={i} className="text-muted-foreground text-base md:text-lg leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-10 md:py-16 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">{props.featuresTitle}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {props.features.map((f, i) => (
              <div key={i} className="flex items-start gap-3 bg-card border border-border rounded-lg p-4">
                <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <span className="text-foreground text-base">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HowTo */}
      <section className="py-10 md:py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">{props.howToTitle}</h2>
          <ol className="space-y-5">
            {props.howToSteps.map((s, i) => (
              <li key={i} className="flex gap-4 bg-card border border-border rounded-lg p-5">
                <div className="shrink-0 w-10 h-10 rounded-full gradient-gold flex items-center justify-center text-accent-foreground font-bold">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{s.name}</h3>
                  <p className="text-muted-foreground text-sm md:text-base leading-relaxed">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Applications */}
      <section className="py-10 md:py-16 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">{props.applicationsTitle}</h2>
          {props.applicationsParagraphs.map((p, i) => (
            <p key={i} className="text-muted-foreground text-base md:text-lg leading-relaxed mb-4">{p}</p>
          ))}
        </div>
      </section>

      {/* FAQ with FAQPage schema */}
      <section className="py-10 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">Najczęściej zadawane pytania</h2>
          <div className="space-y-3">
            {props.faqs.map((faq, i) => (
              <details key={i} className="bg-card border border-border rounded-xl overflow-hidden group">
                <summary className="p-5 font-semibold text-foreground cursor-pointer hover:bg-secondary/50 transition-colors list-none flex items-center justify-between">
                  <span>{faq.q}</span>
                  <span className="text-accent group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <div className="px-5 pb-5 text-muted-foreground leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: props.faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              }),
            }}
          />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-20 gradient-navy">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-primary-foreground mb-4">
            Sprawdź cenę {props.primaryKeyword}
          </h2>
          <p className="text-primary-foreground/80 mb-8 text-base md:text-lg">
            Bezpłatna wycena online w 15 sekund. Bez logowania.
          </p>
          <Link
            to="/kalkulator"
            className="gradient-gold text-accent-foreground font-bold px-10 py-4 rounded-lg hover:opacity-90 transition-opacity shadow-gold inline-flex items-center gap-2 text-lg"
          >
            Wyceń teraz <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default SeoLandingPage;
